import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Scale, DollarSign, MapPin, TrendingUp } from 'lucide-react';
import SeoHead from '@/components/shared/SeoHead';
import PageHero, { HeroStatTile } from '@/components/shared/PageHero';
import AdSlot from '@/components/shared/AdSlot';
import { supabase } from '@/integrations/supabase/client';
import { getStateByCode } from '@/lib/stateUtils';
import { applyRpp } from '@/lib/rpp';
import { generateFAQPageSchema, generateMultipleSchemas, generateWebPageSchema } from '@/lib/schema';

const EXAMPLE_SALARY = 100_000;

const CANONICAL = 'https://wageatlas.com/real-wages/';

const FAQ_ITEMS = [
  {
    question: 'What is the difference between nominal and real wages?',
    answer:
      'A nominal wage is the dollar figure on your paycheck — the number employers advertise and the BLS publishes. A real wage adjusts that figure for local prices, so salaries in different places can be compared by what they actually buy.',
  },
  {
    question: 'What is Regional Price Parity (RPP)?',
    answer:
      'Regional Price Parities are published annually by the U.S. Bureau of Economic Analysis and measure how prices in each state compare with the national average, which is set to 100. A state at 110 is 10% more expensive than average; a state at 90 is 10% cheaper.',
  },
  {
    question: 'How do you convert a nominal wage into a real wage?',
    answer:
      'Divide the nominal wage by the local RPP and multiply by 100. For example, a $100,000 salary in a state with an RPP of 110 is worth about $90,900 in national purchasing power.',
  },
  {
    question: 'Why is the Real Wages toggle disabled on national pages?',
    answer:
      'The United States as a whole is the RPP baseline (100), so real and nominal wages are identical on national pages. The toggle comes alive on state pages, where the comparison is meaningful.',
  },
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Math.round(value));

interface StateRppRow {
  code: string;
  name: string;
  slug: string;
  abbreviation: string;
  rpp: number;
}

const RealWages = () => {
  // Latest state RPPs straight from the BEA-fed table.
  const { data: rppData, isLoading } = useQuery({
    queryKey: ['real-wages-state-rpp'],
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data: latest, error: latestError } = await supabase
        .from('bea_rpp')
        .select('year')
        .neq('area_code', '99')
        .order('year', { ascending: false })
        .limit(1);
      if (latestError) throw latestError;
      const year = latest?.[0]?.year;
      if (!year) return { year: null, states: [] as StateRppRow[] };

      const { data: rows, error } = await supabase
        .from('bea_rpp')
        .select('area_code, rpp_index')
        .eq('year', year)
        .neq('area_code', '99');
      if (error) throw error;

      const states: StateRppRow[] = (rows || [])
        .map((row) => {
          const state = getStateByCode(row.area_code);
          if (!state || !row.rpp_index) return null;
          return {
            code: row.area_code,
            name: state.name,
            slug: state.slug,
            abbreviation: state.abbreviation,
            rpp: row.rpp_index,
          };
        })
        .filter((row): row is StateRppRow => row !== null)
        .sort((a, b) => b.rpp - a.rpp);

      return { year, states };
    },
  });

  const states = rppData?.states ?? [];
  const dataYear = rppData?.year;
  const california = states.find((s) => s.code === '06');
  const caRpp = california?.rpp ?? 110.7;

  return (
    <>
      <SeoHead
        title="Real Wages vs. Nominal Wages: Cost-of-Living Adjusted Pay Explained | Wage Atlas"
        description="What a salary is really worth depends on where you live. Learn how Regional Price Parity (RPP) turns nominal wages into real purchasing power, with the latest cost-of-living index for every state."
        canonical={CANONICAL}
        schemas={generateMultipleSchemas([
          generateWebPageSchema(
            'Real Wages, Explained',
            'How Regional Price Parity turns nominal wages into real purchasing power, with the latest cost-of-living index for every state.',
            CANONICAL,
            {},
          ),
          generateFAQPageSchema(FAQ_ITEMS),
        ])}
      />

      <div className="min-h-screen bg-background">
        <PageHero
          badge={{ icon: Scale, label: 'Methodology' }}
          title={
            <>
              Real Wages, Explained
              <span className="block text-2xl md:text-3xl text-white/80 font-normal mt-2">
                What your salary is actually worth where you live
              </span>
            </>
          }
          subtitle="Every salary table on Wage Atlas has a Real Wages toggle. This page explains what it does, where the data comes from, and why the same paycheck buys more in some states than others."
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
            <HeroStatTile icon={DollarSign} value={formatCurrency(EXAMPLE_SALARY)} label="Example Salary" />
            <HeroStatTile icon={MapPin} value={caRpp.toFixed(1)} label="California RPP" />
            <HeroStatTile
              icon={TrendingUp}
              value={formatCurrency(applyRpp(EXAMPLE_SALARY, caRpp))}
              label="Real Value in CA"
            />
          </div>
        </PageHero>

        <section className="py-12 px-4">
          <div className="container mx-auto max-w-4xl space-y-8">
            <Card>
              <CardHeader>
                <CardTitle>Nominal vs. real wages</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  A <strong className="text-foreground">nominal wage</strong> is the dollar figure on your
                  paycheck — the number employers advertise and the Bureau of Labor Statistics publishes.
                  It says nothing about what those dollars buy.
                </p>
                <p>
                  A <strong className="text-foreground">real wage</strong> adjusts that figure for local
                  prices, so salaries in different places can be compared fairly. A $100,000 offer in
                  San Francisco and a $100,000 offer in Memphis are the same nominal wage but very
                  different standards of living.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>What is Regional Price Parity (RPP)?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  The U.S. Bureau of Economic Analysis measures how much everything — housing, groceries,
                  services — costs in each state relative to the national average, and publishes the result
                  as <strong className="text-foreground">Regional Price Parities</strong>. The national
                  average is set to 100. A state at 110 is 10% more expensive than average; a state at 90
                  is 10% cheaper.
                </p>
                <p>
                  To convert a nominal wage into real purchasing power, we divide by the local RPP and
                  multiply by 100:
                </p>
                <p className="font-mono text-sm bg-muted rounded-md px-4 py-3 text-foreground">
                  real wage = nominal wage × (100 ÷ RPP)
                </p>
                <p>
                  Worked example: California's RPP is {caRpp.toFixed(1)}, so a{' '}
                  {formatCurrency(EXAMPLE_SALARY)} salary there is worth{' '}
                  <strong className="text-foreground">{formatCurrency(applyRpp(EXAMPLE_SALARY, caRpp))}</strong>{' '}
                  in national purchasing power. The same salary in Mississippi (RPP ~87) is worth about{' '}
                  <strong className="text-foreground">{formatCurrency(applyRpp(EXAMPLE_SALARY, 87))}</strong> —
                  the cheaper state effectively gives you a raise.
                </p>
                <p>
                  Because the United States as a whole <em>is</em> the baseline (RPP = 100), real and
                  nominal wages are identical on national pages — that's why the Real Wages toggle is
                  greyed out there. It comes alive on state pages, where the comparison is meaningful.
                </p>
              </CardContent>
            </Card>

            <AdSlot slot="leaderboard" />

            <Card>
              <CardHeader>
                <CardTitle>
                  Cost of living by state{dataYear ? ` (${dataYear} RPP)` : ''}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Sorted from most to least expensive. "Real value of $100k" shows what a{' '}
                  {formatCurrency(EXAMPLE_SALARY)} salary is worth in national purchasing power in each
                  state. Click a state to explore its top-paying occupations.
                </p>
                {isLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 10 }).map((_, i) => (
                      <div key={i} className="h-10 bg-muted animate-pulse rounded" />
                    ))}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>State</TableHead>
                          <TableHead className="text-right">RPP</TableHead>
                          <TableHead className="text-right">vs. U.S. average</TableHead>
                          <TableHead className="text-right">Real value of $100k</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {states.map((state) => (
                          <TableRow key={state.code}>
                            <TableCell>
                              <Link
                                to={`/state/${state.slug}/`}
                                className="font-medium hover:underline underline-offset-2"
                              >
                                {state.name}
                              </Link>
                            </TableCell>
                            <TableCell className="text-right font-mono">{state.rpp.toFixed(1)}</TableCell>
                            <TableCell className="text-right">
                              <span className={state.rpp >= 100 ? 'text-destructive' : 'text-wage-secondary'}>
                                {state.rpp >= 100 ? '+' : '−'}
                                {Math.abs(state.rpp - 100).toFixed(1)}%
                              </span>
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(applyRpp(EXAMPLE_SALARY, state.rpp))}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Where the data comes from</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  Wages come from the BLS Occupational Employment and Wage Statistics (OEWS) program.
                  Regional Price Parities come from the U.S. Bureau of Economic Analysis, which publishes
                  them once a year in December; Wage Atlas ingests the new release automatically, so the
                  Real Wages toggle always uses the latest available index.
                </p>
                <p>
                  Try it yourself: open{' '}
                  <Link to="/salary/software-developers/california/" className="underline underline-offset-2 text-foreground">
                    Software Developer salaries in California
                  </Link>{' '}
                  and flip the Real Wages toggle, or compare every state at once on{' '}
                  <Link to="/best-states/software-developers/" className="underline underline-offset-2 text-foreground">
                    Best States
                  </Link>.
                </p>
                <p>
                  For a deeper walkthrough of comparing job offers across locations — including where
                  the RPP adjustment breaks down — read{' '}
                  <Link to="/insights/cost-of-living-vs-salary/" className="underline underline-offset-2 text-foreground">
                    Cost of Living vs. Salary
                  </Link>{' '}
                  or see{' '}
                  <Link to="/methodology/" className="underline underline-offset-2 text-foreground">
                    our full methodology
                  </Link>.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </>
  );
};

export default RealWages;
